/**
 * ChampCopilot — the VISIBLE floating copilot (FAB + window). Mounted only
 * inside the SMB `Layout` (components/layout/index.js), so it never appears on
 * enterprise/onboarding/upgrade layouts — those carry a different access token
 * than the child-location token the copilot needs.
 *
 * State lives in CopilotProvider (mounted at the app shell, above the per-route
 * Layout) so open/closed + the active conversation + the WS survive navigation
 * even though this component remounts with Layout on each route change.
 *
 * Gated behind the `webCopilot` ConfigCat flag AND the `copilot` product
 * entitlement; renders nothing for non-entitled / flagged-off users.
 *
 * Fixed v1 configuration: native theme · window layout · voice-wave launcher ·
 * wave voice visualizer (visual-only) · agent-step preview from real traces.
 */
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useFeatureVal } from '@app/helpers/hooks'
import { isEnabled } from '@helpers/utility'
import config from '@config/index'
import { useCopilotContext } from './CopilotContext'
import CopilotLauncher from './CopilotLauncher'
import CopilotWindow from './CopilotWindow'

const COPILOT_PRODUCT = 'copilot'
// `webCopilot` lives in the ConfigCat *extension* config, so it's not in the UI's
// local featureFlags registry — passing an inline flag makes useFeatureVal resolve
// it via the backend (/feature-flag/customer) instead of the client SDK.
const WEB_COPILOT_FLAG = { name: 'webCopilot', default: false }

const ChampCopilot: React.FC = () => {
  const me = useSelector((state: { auth: { me?: { _id?: string } } }) => state.auth.me)
  const products = useSelector((state: { auth: { products?: string[] } }) => state.auth.products || [])
  const flagEnabled = useFeatureVal(WEB_COPILOT_FLAG)

  // open state + conversation live in the provider so they survive the Layout
  // remount that happens on every in-app navigation
  const { copilot, open, setOpen } = useCopilotContext()

  const hasMe = Boolean(me && me._id)
  const productEnabled = isEnabled(COPILOT_PRODUCT, products)
  const entitled = hasMe && productEnabled && flagEnabled

  // TEMP debug — why the FAB does/doesn't render. Remove before prod rollout.
  useEffect(() => {
    console.log('[ChampCopilot] gate', {
      hasMe,
      meId: me && me._id,
      product: COPILOT_PRODUCT,
      productEnabled,
      products,
      flagKey: WEB_COPILOT_FLAG.name,
      flagEnabled,
      copilotWsUrl: (config as { copilot_ws_url?: string }).copilot_ws_url,
      entitled
    })
  }, [hasMe, productEnabled, flagEnabled, entitled, me, products])

  if (!entitled) return null

  const handleOpen = () => {
    setOpen(true)
    copilot.connect()
  }

  return (
    <>
      <CopilotLauncher hidden={open} onClick={handleOpen} />
      <CopilotWindow open={open} copilot={copilot} onClose={() => setOpen(false)} />
    </>
  )
}

export default ChampCopilot
